/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { MutableRef } from 'preact/hooks';
import { useUser } from '#hooks/UNSAFE_useUser';
import { handleInput, cancelEvent } from './events';
import { CommitDetail, InputDetail } from '#utils/UNSAFE_meterUtils';
import { adjustForStep } from '#utils/PRIVATE_meterUtils/meterUtils';

export function useKeyboardEvents(
  value: number,
  min: number,
  max: number,
  step: number,
  currentInputRef: MutableRef<number | undefined>,
  onCommit?: (detail: CommitDetail) => void,
  onInput?: (detail: InputDetail) => void
) {
  const { direction } = useUser();

  const isRtl = direction === 'rtl';

  const stepIncrementedVal = adjustForStep(value + step, step, min, max);
  const stepDecrementedVal = adjustForStep(value - step, step, min, max);

  const keyDownHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Tab':
        return;
      case 'ArrowDown':
        handleInput(Math.max(min, stepDecrementedVal), currentInputRef, onInput);
        cancelEvent(event);
        break;
      case 'ArrowUp':
        handleInput(Math.min(max, stepIncrementedVal), currentInputRef, onInput);
        cancelEvent(event);
        break;
      case 'ArrowLeft': {
        const inputValue = isRtl
          ? Math.min(max, stepIncrementedVal)
          : Math.max(min, stepDecrementedVal);
        handleInput(inputValue, currentInputRef, onInput);
        cancelEvent(event);
        break;
      }
      case 'ArrowRight': {
        const inputValue = isRtl
          ? Math.max(min, stepDecrementedVal)
          : Math.min(max, stepIncrementedVal);
        handleInput(inputValue, currentInputRef, onInput);
        cancelEvent(event);
        break;
      }
    }
  };

  const keyUpHandler = (event: KeyboardEvent) => {
    const key = event.key;

    switch (key) {
      case 'Enter':
        onCommit?.({ value: value } as CommitDetail);
        cancelEvent(event);
        break;
      case 'Tab':
        handleInput(value, currentInputRef, onInput);
        cancelEvent(event);
        break;
      case 'Home':
        handleInput(min, currentInputRef, onInput);
        cancelEvent(event);
        break;
      case 'End':
        handleInput(max, currentInputRef, onInput);
        cancelEvent(event);
        break;
    }
  };

  return {
    onKeyUp: keyUpHandler,
    onKeyDown: keyDownHandler
  };
}
